import { useEffect, useState } from 'react';
import { Button, IconButton } from '@carbon/react';
import { Close } from '@carbon/react/icons';
import MonthPicker from 'components/month-picker/month-picker';
import { parseMomentUtc, SERVER_DATE_FORMAT } from 'utils/dates';
import type { CashflowPeriod } from './types';
import styles from './cashflow-period-editor.module.scss';

/** The in-progress draft — either bound may be unset until the analyst picks it. */
interface PeriodDraft {
  start: string | null;
  end: string | null;
}

export interface CashflowPeriodEditorProps {
  /** Whether the editor is shown (the parent gear/menu owns visibility). */
  open: boolean;
  /** The currently-applied period, or null when none is set. */
  value: CashflowPeriod | null;
  /** Apply the draft window (both bounds set, start ≤ end) — first-of-month `yyyy-MM-dd`. */
  onApply: (period: CashflowPeriod) => void;
  /** Discard the draft and close (the applied value is untouched). */
  onCancel: () => void;
  /** Clear the applied period (only offered when one is set). */
  onRemove: () => void;
}

/**
 * Builds a first-of-month `yyyy-MM-dd` from a MonthPicker's selection.
 *
 * @param {number} month - The 0-indexed month (MonthPicker / moment convention).
 * @param {number} year - The full year.
 * @return {string} The first-of-month date string (`YYYY-MM-DD`).
 */
function firstOfMonth(month: number, year: number): string {
  return parseMomentUtc(`${year}-01-01`)
    .month(month)
    .format(SERVER_DATE_FORMAT);
}

/**
 * Seeds an editor draft from the applied value (both bounds null when none is set).
 *
 * @param {CashflowPeriod | null} value - The applied period, or null.
 * @return {PeriodDraft} The draft to edit from.
 */
function toDraft(value: CashflowPeriod | null): PeriodDraft {
  return value
    ? { start: value.start, end: value.end }
    : { start: null, end: null };
}

/**
 * The From→To cashflow-period editor used by both cards (per-card instance). Two free
 * month/year `MonthPicker`s (fixed width) over a local draft — Remove + Apply inline, and
 * Cancel as a dismiss ✕ top-right — purely presentational (data + callbacks in; the parent
 * owns the applied value + visibility).
 * There is no live preview: the applied slice-vs-total summary lives on the card, from
 * the query response.
 *
 * The draft re-seeds from `value` whenever the editor opens (so a cancelled session
 * doesn't leak into the next open) — while open, in-progress edits are preserved
 * (`value` only changes on Apply/Remove, which the parent drives).
 *
 * @param {CashflowPeriodEditorProps} props - Visibility, the applied value, and the apply/cancel/remove callbacks.
 * @return {JSX.Element | null} The editor, or null when closed.
 */
export function CashflowPeriodEditor({
  open,
  value,
  onApply,
  onCancel,
  onRemove,
}: CashflowPeriodEditorProps) {
  const [draft, setDraft] = useState<PeriodDraft>(() => toDraft(value));

  // Re-seed the draft from the applied value each time the editor opens (or the value
  // changes) — discards a prior cancelled draft; a no-op after Apply (value === draft).
  useEffect(() => {
    if (open) {
      setDraft({ start: value?.start ?? null, end: value?.end ?? null });
    }
    // Depend on the bounds, not the `value` object identity, so an equal-but-new period
    // reference (e.g. a future memo drop) can't clobber an in-progress draft.
  }, [open, value?.start, value?.end]);

  if (!open) {
    return null;
  }

  const isValid =
    draft.start !== null && draft.end !== null && draft.start <= draft.end;

  return (
    <div className={styles.editor}>
      <MonthPicker
        className={styles.picker}
        inputClassName={styles.pickerInput}
        light
        label="From"
        selectedMonth={draft.start ? parseMomentUtc(draft.start) : undefined}
        setSelectedMonth={(month, year) =>
          setDraft((current) => ({
            ...current,
            start: firstOfMonth(month, year),
          }))
        }
      />
      <MonthPicker
        className={styles.picker}
        inputClassName={styles.pickerInput}
        light
        label="To"
        selectedMonth={draft.end ? parseMomentUtc(draft.end) : undefined}
        setSelectedMonth={(month, year) =>
          setDraft((current) => ({
            ...current,
            end: firstOfMonth(month, year),
          }))
        }
      />
      {value !== null && (
        <Button
          className={styles.action}
          kind="danger--ghost"
          size="sm"
          onClick={onRemove}
        >
          Remove
        </Button>
      )}
      <Button
        className={styles.action}
        kind="primary"
        size="sm"
        disabled={!isValid}
        onClick={() => {
          // Inline null-check narrows `string | null` → `string` (the `isValid`
          // const alone doesn't narrow); `disabled` already blocks start > end.
          if (draft.start !== null && draft.end !== null) {
            onApply({ start: draft.start, end: draft.end });
          }
        }}
      >
        Apply
      </Button>
      <span className={styles.close}>
        <IconButton
          kind="ghost"
          size="sm"
          label="Close"
          align="left"
          onClick={onCancel}
        >
          <Close />
        </IconButton>
      </span>
    </div>
  );
}
